import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NbDialogService } from '@nebular/theme';
import { UiBlueprintDesignerControllerService } from 'src/app/cc-api/services';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';
import { ContextUpdatesService } from 'src/app/services/context-updates.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';

@Component({
  selector: 'app-branch-selector',
  templateUrl: './branch-selector.component.html',
  styleUrls: ['./branch-selector.component.scss'],
})
export class BranchSelectorComponent implements OnChanges {
  @Input() blueprintName: string;
  @Input() allBranches: string[] = []; // All branches for the current blueprint
  @Input() defaultBranch: string; // The default branch for the current blueprint
  @Input() selectedBranch: string; // The selected branch for the current blueprint
  @Input() nextSelectedBranch: string; // The branch which the user clicks on out of the drop down
  @Input() contextSwitcherOpen: boolean = false; // If the context switcher is open then the branch selector should not open
  @Input() fetchingBranches: boolean = false; // Flag for checking if fetching branch API is in progress
  @Output() byBranchSelectionChange: EventEmitter<any> =
    new EventEmitter<any>(); // Emits an event whenever the branch is switched
  @Output() byBranchSelectorOpen: EventEmitter<any> = new EventEmitter<any>(); // Sends a signal to the parent component that the branch selector is open

  errorIcon: string = './assets/error.svg';

  branchSelectorOpen: boolean = false; // Marks the flag if the branch selector needs to be open
  searchedBranch: string; // Branch name inside the search box
  allBranchesBackup: string[] = []; // A backup storage so that after search key is removed we can go back to original data
  branchForm: UntypedFormGroup; // Form Group for branch
  fb: UntypedFormBuilder; // Form builder for making branchForm form

  @ViewChild('addNewBranchDialog') addNewBranchDialog: TemplateRef<any>; // Dialog ref for adding a new branch
  addingNewBranch: boolean = false; // Is set to true when API call to add new branch is in progress

  constructor(
    private dialogService: NbDialogService,
    private blueprintDesignerService: UiBlueprintDesignerControllerService,
    private customToast: CustomToastService,
    private contextUpdatesService: ContextUpdatesService,
    public featureFlagsService: FeatureFlagsService,
  ) {
    this.fb = new UntypedFormBuilder();
    this.branchForm = this.fb.group({
      branchName: new UntypedFormControl('', [Validators.required]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes?.defaultBranch?.currentValue !=
      changes?.defaultBranch?.previousValue
    ) {
      let lastOpenBranch =
        this.selectedBranch ||
        this.contextUpdatesService.getLastBranchForBlueprint(
          this.blueprintName,
        );
      if (!lastOpenBranch || !this.allBranches?.includes(lastOpenBranch)) {
        lastOpenBranch = changes.defaultBranch.currentValue;
        this.contextUpdatesService.setLastOpenedBranchInLocalStorage(
          this.blueprintName,
          !this.featureFlagsService.getBranchSelectorFlag()
            ? this.defaultBranch
            : lastOpenBranch,
        );
      }

      // Selected Branch would be the one which we would be dealing with mostly in this component so we
      // set that as the selectedBranch
      this.selectedBranch = lastOpenBranch;
    }
    if (
      changes?.allBranches?.currentValue != changes?.allBranches?.previousValue
    ) {
      this.allBranchesBackup = this.allBranches;
    }
    if (changes?.contextSwitcherOpen?.currentValue) {
      this.branchSelectorOpen = false;
    }
  }

  onBranchChange(branch: string) {
    this.nextSelectedBranch = branch;
  }

  applyBranchChange() {
    this.toggleBranchSelector();
    this.byBranchSelectionChange.emit(this.nextSelectedBranch);
  }

  onBranchSearchInput(searchKey: string) {
    if (!searchKey?.length) {
      this.allBranches = this.allBranchesBackup;
    }
    searchKey = searchKey?.toLowerCase();
    this.allBranches = this.allBranchesBackup.filter((branch) =>
      branch?.toLowerCase()?.includes(searchKey),
    );
  }

  toggleBranchSelector(event?) {
    this.branchSelectorOpen = !this.branchSelectorOpen;
    event?.stopPropagation();

    if (this.branchSelectorOpen) {
      this.nextSelectedBranch = this.selectedBranch;
      this.byBranchSelectorOpen.emit(true);
    }
  }

  openNewBranchForm() {
    this.branchForm.reset();
    this.dialogService.open(this.addNewBranchDialog, {
      closeOnBackdropClick: false,
      hasBackdrop: true,
    });
  }

  get branchName() {
    return this.branchForm.get('branchName');
  }

  // Creates a new branch and emits and updates the allBranches list
  createNewBranch(ref) {
    this.addingNewBranch = true;
    this.blueprintDesignerService
      .createBranchUsingPOST({
        stackName: this.blueprintName,
        branch: this.branchName?.value,
      })
      .subscribe(
        () => {
          this.customToast.success({
            content: 'New branch has been created.',
          });
          this.allBranches?.push(this.branchName?.value);
          this.onBranchChange(this.branchName?.value);
          this.applyBranchChange();
          ref?.close();
        },
        (error) => {
          this.customToast.error({
            content:
              error?.error?.message ||
              `New branch ${this.branchName?.value} could not be created`,
          });
        },
      )
      .add(() => {
        this.addingNewBranch = false;
      });
  }
}
